import useApi from "@toothfairy/shared-api/useApi";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useReactiveVar } from "react-reactive-var";
import { showSideMenu, userType } from "../../../App";
import envConfig from "../../../envConfig";
import usersAPI from "../../API/Users/usersAPI";
import workspacesHelper from "../../API/Workspaces/workspacesHelper";
import usePath from "../../Hooks/usePath";
import useWorkspaces from "../../Hooks/useWorkspaces";
import { lastMembersChange } from "../../Screens/MainViewContainer";
import { UserType } from "../../models";
import UsersInvite from "./UsersInvite";
import workspacesAPI from "../../API/Workspaces/workspacesAPI";

dayjs.extend(relativeTime);
function onlyUnique(value, index, self) {
  return self.indexOf(value) === index;
}
const UsersInviteContainer = () => {
  const showMenu = useReactiveVar(showSideMenu);
  const [showModal, setShowModal] = useState(false);
  const [emailValidated, setEmailValidated] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedUserType, setSelectedUserType] = useState("User");
  const [selectedUserName, setSelectedUserName] = useState(null);
  const [selectedUserID, setSelectedUserID] = useState(null);
  const [changeInProgress, setChangeInProgress] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [newUsers, setNewUsers] = useState([]);
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const [usersAccess, setUsersAccess] = useState([]);
  const [showChoice, setShowChoice] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [showBulkDeleteChoice, setShowBulkDeleteChoice] = useState(false);
  const [forcedValidationError, setForcedValidationError] = useState(null);
  const {
    getActiveWorkspace,
    isUserAdmin,
    isUserRoot,
    isPowerUserType,
    canAddUserOfType,
    getMaxUsers,
    getMaxPowerUsers,
  } = useWorkspaces();
  const { user } = AppUser.useUser();
  const [shareMembers, setShareMembers] = useState([]);
  const AppToasty = AppToast.useToast();
  const [shareMembersRef, setShareMembersRef] = useState([]);
  const { updatePath } = usePath();
  const { t } = useTranslation();
  const [shareInputValue, setShareInputValue] = useState("");
  const [usersCount, setUsersCount] = useState(1);
  const {
    data: usersUpdateResult,
    error: usersUpdateError,
    loading: usersUpdateInProgress,
    apiRequest: usersUpdateRequest,
    response: usersUpdateResponse,
  } = useApi(workspacesHelper.updateWorkspaceUsers, envConfig);

  const {
    data: userEmailRequestResult,
    error: userEmailRequestError,
    loading: userEmailRequestInProgress,
    apiRequest: userEmailRequestFetchRequest,
    response: userEmailRequestResponse,
  } = useApi(usersAPI.getUserByEmail, envConfig);

  const loadMembers = async () => {
    if (getActiveWorkspace()?.users) {
      const result = await workspacesAPI.getWorkspaceUsers(
        getActiveWorkspace()?.id
      );
      const data = result?.data?.getUsersWorkspace;
      const sharedWorkspaceUsers =
        data?.map((item) => ({
          email: item.user_email,
          username: item.username,
          type:
            item.id !== getActiveWorkspace().userID
              ? t("usersInvite.userTypes.member")
              : t("usersInvite.userTypes.admin"),
          id: item.id,
        })) || [];
      setUsersCount(sharedWorkspaceUsers?.length || 1);
      setShareMembers([...sharedWorkspaceUsers]);
      setShareMembersRef([...sharedWorkspaceUsers]);
    }
  };

  const fetchAllUsersAccess = async () => {
    const maxTotalUsers = getMaxUsers() + getMaxPowerUsers();
    let allItems = [];
    let nextToken = null;
    do {
      const result = await usersAPI.getWorkspaceUsersByWorkspaceID(
        getActiveWorkspace().id,
        maxTotalUsers,
        nextToken
      );
      const page = result?.data?.userToWorkspaceMapping;
      allItems = allItems.concat(page?.items || []);
      nextToken = page?.nextToken || null;
    } while (nextToken);
    return allItems.filter((u) => u?._deleted !== true);
  };

  const loadMembersAccess = async () => {
    if (getActiveWorkspace()?.id) {
      setUsersLoading(true);
      setUsersAccess(await fetchAllUsersAccess());
      setUsersLoading(false);
    }
  };

  const powerUsersCount = shareMembers.filter((member) => {
    if (member?.id === getActiveWorkspace()?.userID) return true;
    const mapping = usersAccess.find((u) => u.userID === member?.id);
    return mapping ? isPowerUserType(mapping.userType) : false;
  }).length;
  const regularUsersCount = shareMembers.length - powerUsersCount;

  const handleUserDeletion = async () => {
    try {
      if (selectedItem?.id === getActiveWorkspace()?.userID) return;
      const members = shareMembers.filter((u) => u.id !== selectedItem?.id);
      const remainingIds = members?.map((member) => member?.id);
      const mappingUserWorkspace = usersAccess.find(
        (u) => u.userID === selectedItem?.id
      );
      if (mappingUserWorkspace?.id) {
        await usersAPI.deleteWorkspaceUser(
          mappingUserWorkspace.id,
          mappingUserWorkspace._version
        );
      }
      await workspacesHelper.updateWorkspace(getActiveWorkspace().id, [
        { property: "users", value: remainingIds },
      ]);
      setShareMembers([...members]);
      setShowModal(false);
      setShowChoice(false);
      loadMembers();
      loadMembersAccess();
      AppToasty.show(t("usersInvite.toast.deleteSuccess"), {
        type: "success",
      });
    } catch (error) {
      console.error("[UsersInvite] DELETE failed:", error);
      setShowChoice(false);
      loadMembers();
      loadMembersAccess();
      AppToasty.show(t("usersInvite.toast.deleteFailed"), {
        type: "danger",
      });
    }
  };
  const handleMembersBulkDeletion = async () => {
    try {
      const ownerId = getActiveWorkspace()?.userID;
      let remainingMembers = [...shareMembers];
      for (const id of selectedUserIds) {
        const mappingUserWorkspace = usersAccess.find((u) => u.userID === id);
        const isOwner =
          id === ownerId || mappingUserWorkspace?.userType === UserType.ROOT;
        if (isOwner) continue;
        if (mappingUserWorkspace?.id) {
          await usersAPI.deleteWorkspaceUser(
            mappingUserWorkspace.id,
            mappingUserWorkspace._version
          );
        }
        remainingMembers = remainingMembers.filter((u) => u.id !== id);
      }
      await workspacesHelper.updateWorkspace(getActiveWorkspace().id, [
        {
          property: "users",
          value: remainingMembers?.map((member) => member?.id),
        },
      ]);
      setShareMembers([...remainingMembers]);
      setSelectedUserIds([]);
      setShowBulkDeleteChoice(false);
      loadMembers();
      loadMembersAccess();
      AppToasty.show(t("usersInvite.toast.deleteSuccess"), {
        type: "success",
      });
    } catch (error) {
      console.error("[UsersInvite] BULK DELETE failed:", error);
      setShowBulkDeleteChoice(false);
      loadMembers();
      loadMembersAccess();
      AppToasty.show(t("usersInvite.toast.deleteFailed"), {
        type: "danger",
      });
    }
  };
  const handleMemberAddition = async (payload) => {
    try {
      setEmailValidated(true);
      const enhancedPayload = {
        ...payload,
        email: shareInputValue,
      };
      setNewUsers([...newUsers, enhancedPayload]);
      setSelectedUserName(payload?.label);
      setSelectedUserID(payload?.value);
    } catch (error) {
      setEmailValidated(false);
      setForcedValidationError(t("usersInvite.errors.userNotFound"));
    }
  };
  const handleUserSelection = (value) => {
    const userId = value?.id || value?.item?.id;
    const userMapping = usersAccess.find((u) => u.userID === userId);
    setSelectedUserType(userMapping?.userType || "User");
    setSelectedItem(value);
    setEmailValidated(true);
    setSelectedUserName(value?.label || value?.item?.label);
    setShowModal(true);
  };
  const handleMembersInvite = async () => {
    try {
      setChangeInProgress(true);
      if (isEditMode) {
        const userMapping = usersAccess.find(
          (u) => u.userID === selectedItem?.id
        );
        const currentUserType = userMapping?.userType;
        if (
          currentUserType !== selectedUserType &&
          !canAddUserOfType(selectedUserType, usersAccess)
        ) {
          AppToasty.show(
            t("legacy.WorkspaceMembersLimitReachedCaption") ||
              "You have reached the maximum number of users for this type",
            { type: "danger" }
          );
          setChangeInProgress(false);
          return;
        }
        const updateResult = await usersAPI.updateWorkspaceUser(
          userMapping?.id,
          userMapping?._version,
          selectedUserType
        );
        const refreshedAccess = await fetchAllUsersAccess();
        const indexOf = shareMembers.findIndex(
          (u) => u.id === selectedItem?.id
        );
        let newMembers = [...shareMembers];
        newMembers[indexOf].type = selectedUserType;
        setShareMembers([...newMembers]);

        setUsersAccess(refreshedAccess);
        setShowModal(false);
      } else {
        const newUsersStack = [
          ...shareMembers,
          ...newUsers?.map((user) => ({
            id: user?.value,
            email: user?.email,
            username: user?.label,
            type: selectedUserType,
            status: t("usersInvite.status.toInvite"),
          })),
        ];

        // Detect shareMembersRef and shareMembers differences and create 3 arrays.
        // 1. New members to be invited
        // 2. Members to be removed
        // 3. Members to be updated
        const newMembers = newUsersStack.filter(
          (member) => !shareMembersRef.find((ref) => ref.id === member.id)
        );
        // for each new member, send an invite
        for (const member of newMembers) {
          const resultCreation = await usersAPI.createWorkspaceUser(
            getActiveWorkspace().id,
            member.id,
            member.type
          );
        }
        const result = await usersUpdateRequest({
          workspaceid: getActiveWorkspace().id,
          users: newUsersStack.map((member) => member?.id),
        });
        lastMembersChange(dayjs().toDate().getTime());
        if (result?.emailsNotFound?.length > 0) {
          AppToasty.show(t("legacy.WorkspaceMembersNotFoundUpdateCaption"), {
            type: "warning",
          });
        } else {
          loadMembers();
          loadMembersAccess();
          AppToasty.show(t("legacy.WorkspaceMembersUpdateCaption"), {
            type: "success",
          });
        }
        setShowModal(false);
        setNewUsers([]);
        setShareMembers(newUsersStack);
      }
    } catch (error) {
      setShowModal(false);
      AppToasty.show(t("legacy.WorkspaceMembersUpdateFailedCaption"), {
        type: "danger",
      });
    } finally {
      setChangeInProgress(false);
    }
  };
  useEffect(() => {
    if (getActiveWorkspace()?.id) {
      loadMembers();
      loadMembersAccess();
    }
  }, [getActiveWorkspace()?.id, JSON.stringify(getActiveWorkspace()?.users)]);
  useEffect(() => {
    setForcedValidationError(null);
  }, [shareInputValue]);

  const validateSelection = (user) => {
    const userId = user?.id || user?.item?.id;
    const userMapping = usersAccess.find((u) => u.userID === userId);
    const userTypeValue = userMapping?.userType || user?.userType || user?.type;
    if (userTypeValue == UserType.ROOT) {
      AppToasty.show(t("usersInvite.errors.rootCannotEdit"), {
        placement: "bottom",
        type: "danger",
      });
      return false;
    } else if (userTypeValue == UserType.SUPER_ADMIN && !isUserRoot()) {
      AppToasty.show(t("usersInvite.errors.superAdminEditOnlyByRoot"), {
        placement: "bottom",
        type: "danger",
      });
      return false;
    } else if (!isUserRoot() && !isUserAdmin()) {
      AppToasty.show(t("usersInvite.errors.onlyRootOrAdmin"), {
        placement: "bottom",
        type: "danger",
      });
      return false;
    } else {
      return true;
    }
  };
  return (
    <UsersInvite
      dispatch={dispatch}
      showMenu={showMenu}
      showModal={showModal}
      setShowModal={setShowModal}
      updatePath={updatePath}
      shareMembersRef={shareMembersRef}
      shareMembers={shareMembers}
      shareInputValue={shareInputValue}
      newUsers={newUsers}
      setShareInputValue={setShareInputValue}
      handleMembersInvite={handleMembersInvite}
      handleMemberAddition={handleMemberAddition}
      handleMemberDelete={handleUserDeletion}
      isAdmin={isUserAdmin()}
      forcedValidationError={forcedValidationError}
      userEmailRequestInProgress={userEmailRequestInProgress}
      setShowChoice={setShowChoice}
      showChoice={showChoice}
      usersAccess={usersAccess}
      selectedUserType={selectedUserType}
      setSelectedUserType={setSelectedUserType}
      handleCreation={() => {
        setEmailValidated(false);
        setShareInputValue("");
        setSelectedUserType("User");
        setSelectedUserName(null);
        setSelectedUserID(null);
        setShowModal(true);
        setIsEditMode(false);
      }}
      changeInProgress={changeInProgress}
      setSelectedUserName={setSelectedUserName}
      selectedUserName={selectedUserName}
      emailValidated={emailValidated}
      usersLoading={usersLoading}
      selectedIndex={selectedIndex}
      setSelectedIndex={setSelectedIndex}
      selectedItem={selectedItem}
      setSelectedItem={setSelectedItem}
      validateSelection={validateSelection}
      handleUserSelection={handleUserSelection}
      userEmailRequestFetchRequest={userEmailRequestFetchRequest}
      isEditMode={isEditMode}
      setIsEditMode={setIsEditMode}
      selectedUserIds={selectedUserIds}
      setSelectedUserIds={setSelectedUserIds}
      showBulkDeleteChoice={showBulkDeleteChoice}
      setShowBulkDeleteChoice={setShowBulkDeleteChoice}
      handleMembersBulkDelete={handleMembersBulkDeletion}
      powerUsersCount={powerUsersCount}
      regularUsersCount={regularUsersCount}
      canAddUserOfType={(userType) => canAddUserOfType(userType, usersAccess)}
    />
  );
};

export default UsersInviteContainer;
